import { createBrowserRouter } from 'react-router-dom';
import { LoginPage } from './unauthorized/LoginPage';
import { RootPage } from './authorized/rootPage/RootPage.tsx';
import { Links } from 'application/constants/links';
import { UiKitPage } from './authorized/uikit/UiKitPage';
import { ReactRouterErrorBoundary } from './ReactRouterErrorBoundary';
import { WelcomePage } from './authorized/welcome/Welcome';
import { AdminPage } from './authorized/admin/AdminPage';
import { ArticlesPage } from './authorized/articles/ArticlesPage';
import { PlanChartPage } from './authorized/plan-chart/PlanChart';
import { FirmwarePage } from './authorized/firmware/Firmware';
import { DesignerRoot } from './authorized/page-designer/DesignerRoot';
import { SinglePage } from './authorized/page-designer/SinglePage';
import { DeliveryObligationPage } from './authorized/delivery-obligation/DeliveryObligationPage';
import { QuerySuspenseErrorWrapper } from 'helpers/retry-helper';
import ErrorPage from './unauthorized/errorPages/ErrorPage';
import { DruckdatenPage } from './authorized/druckdaten/DruckdatenPage';
import { WarrantyRequestPage } from './authorized/warranty-request/WarrantyRequestPage.tsx';
import { WarrantyRequestHistoryPage } from './authorized/warranty-request-history/WarrantyRequestHistoryPage.tsx';
import { DesignerNewPage } from './authorized/page-designer/DesignerNewPage.tsx';
import ContactsPage from './unauthorized/ContactsPage.tsx';
import ProtectedRoute from '../helpers/router/ProtectedRoute.tsx';
import { ResetPasswordPage } from './unauthorized/ResetPasswordPage.tsx';
import { Qualifications } from './authorized/qualifications/Qualifications.tsx';
import React from 'react';
import { ForgotPasswordPage } from './unauthorized/ForgotPasswordPage.tsx';
import { DeviceOverview } from './authorized/device-overview/DeviceOverview.tsx';
import { UserManagement } from './authorized/user-management/UserManagement.tsx';
import { OrderList } from './authorized/order-list/OrderList.tsx';
import { OutstandingReceivables } from './authorized/outstanding-receivables/OutstandingReceivables.tsx';
import { Repairs } from './authorized/repairs/Repairs.tsx';
import { ServiceAndRepair } from './authorized/service-and-repair/ServiceAndRepair.tsx';
import { TelehealthBilling } from './authorized/telehealth-billing/TelehealthBilling.tsx';
import { ServicePresentation } from './authorized/service-presentation/ServicePresentation.tsx';
import { PriceLists } from './authorized/price-lists/PriceLists.tsx';
import { TechUpdates } from './authorized/tech-updates/TechUpdates.tsx';
import { SNLots } from './authorized/sn-and-lots/SNLots.tsx';
import { WarrantyInfo } from './authorized/warranty-info/WarrantyInfo.tsx';
import { UseCookieAuth } from 'helpers/auth/auth-settings.ts';
import { ServerSideLoginPage } from './unauthorized/ServerSideLoginPage.tsx';
import { MainboardReplacerForm } from './authorized/MainboardReplacer/MainboardReplacer.tsx';
import { AbasDocuments } from './authorized/abas-documents/AbasDocuments.tsx';
import { AdvancedSearch } from './authorized/advanced-search/AdvancedSearch.tsx';
import { FeatureFlags } from '../application/constants/env-variables.ts';
import { CertificateInstruction } from './authorized/certificate-instruction/CertificateInstruction.tsx';
import { DetailedRepairs } from './authorized/repairs/DetailedRepairs.tsx';
import { CreateRepairRequest } from './authorized/create-repair-request/CreateRepairRequest.tsx';
import { AllowedRoles } from '../application/constants/allowed-roles.ts';
import { AccountSettings } from './authorized/account-settings/AccountSettings.tsx';

export const authorizedRoutes = () =>
  createBrowserRouter([
    {
      path: '/',
      element: <RootPage />,
      children: [
        { path: Links.Authorized.UiKit.route, element: <UiKitPage /> },
        {
          path: '',
          element: (
            <ProtectedRoute allowedRoles={AllowedRoles.WelcomePage}>
              <WelcomePage />
            </ProtectedRoute>
          ),
        },
        { path: Links.Authorized.Welcome.route, element: <WelcomePage /> },
        {
          path: Links.Authorized.Admin.route,
          element: (
            <ProtectedRoute allowedRoles={AllowedRoles.AdminPage}>
              <AdminPage />
            </ProtectedRoute>
          ),
        },
        {
          path: Links.Authorized.Articles.route,
          element: (
            <ProtectedRoute allowedRoles={AllowedRoles.ArticlesPage}>
              <ArticlesPage />
            </ProtectedRoute>
          ),
        },
        {
          path: Links.Authorized.FirmwareOld.route,
          element: (
            <ProtectedRoute allowedRoles={AllowedRoles.FirmwareOldPage}>
              <FirmwarePage />
            </ProtectedRoute>
          ),
        },
        {
          path: Links.Authorized.DeliveryObligation.route,
          element: (
            <ProtectedRoute allowedRoles={AllowedRoles.DeliveryObligationPage}>
              <DeliveryObligationPage />
            </ProtectedRoute>
          ),
        },
        { path: Links.Authorized.PlanChart.route, element: <PlanChartPage /> },
        {
          path: Links.Authorized.Druckdaten.route,
          element: (
            <ProtectedRoute allowedRoles={AllowedRoles.DruckdatenPage}>
              <DruckdatenPage />
            </ProtectedRoute>
          ),
        },
        {
          path: Links.Authorized.CustomPages.DesignerRoot.route,
          element: (
            <ProtectedRoute allowedRoles={AllowedRoles.DesignerRootPage}>
              <DesignerRoot />
            </ProtectedRoute>
          ),
        },
        {
          path: Links.Authorized.CustomPages.DesignerNewPage.route,
          element: (
            <ProtectedRoute allowedRoles={AllowedRoles.DesignerNewPage}>
              <DesignerNewPage />
            </ProtectedRoute>
          ),
        },
        {
          path: Links.Authorized.CustomPages.DesignerSinglePage.route,
          element: (
            <ProtectedRoute allowedRoles={AllowedRoles.DesignerSinglePage}>
              <DesignerNewPage />
            </ProtectedRoute>
          ),
        },
        {
          path: Links.Authorized.CustomPages.SinglePage.route,
          element: <SinglePage />,
        },
        {
          path: '/page/:url',
          element: <SinglePage />,
        },
        {
          path: Links.Authorized.WarrantyRequest.route,
          element: (
            <ProtectedRoute allowedRoles={AllowedRoles.WarrantyRequestPage}>
              <WarrantyRequestPage />
            </ProtectedRoute>
          ),
        },
        {
          path: Links.Authorized.WarrantyRequestHistory.route,
          element: (
            <ProtectedRoute
              allowedRoles={AllowedRoles.WarrantyRequestHistoryPage}
            >
              <WarrantyRequestHistoryPage />
            </ProtectedRoute>
          ),
        },
        {
          path: '*',
          element: <ErrorPage />,
        },
        {
          path: Links.Authorized.Qualifications.route,
          element: (
            <ProtectedRoute allowedRoles={AllowedRoles.QualificationsPage}>
              <Qualifications />
            </ProtectedRoute>
          ),
        },
        {
          path: Links.Authorized.DeviceOverview.route,
          element: (
            <ProtectedRoute allowedRoles={AllowedRoles.DeviceOverviewPage}>
              <DeviceOverview />
            </ProtectedRoute>
          ),
        },
        {
          path: Links.Authorized.UserManagement.route,
          element: (
            <ProtectedRoute allowedRoles={AllowedRoles.UserManagementPage}>
              <UserManagement />
            </ProtectedRoute>
          ),
        },
        {
          path: Links.Authorized.OrderList.route,
          element: (
            <ProtectedRoute allowedRoles={AllowedRoles.OrderListPage}>
              <OrderList />
            </ProtectedRoute>
          ),
        },
        {
          path: Links.Authorized.OutstandingReceivables.route,
          element: (
            <ProtectedRoute
              allowedRoles={AllowedRoles.OutstandingReceivablesPage}
            >
              <OutstandingReceivables />
            </ProtectedRoute>
          ),
        },
        {
          path: FeatureFlags.isCreateRepairRequestEnabled()
            ? Links.Authorized.CreateRepairRequest.route
            : undefined,
          element: (
            <ProtectedRoute allowedRoles={AllowedRoles.CreateRepairRequestPage}>
              <CreateRepairRequest />
            </ProtectedRoute>
          ),
        },
        {
          path: Links.Authorized.DetailedRepairs.route,
          element: (
            <ProtectedRoute allowedRoles={AllowedRoles.DetailedRepairsPage}>
              <DetailedRepairs />
            </ProtectedRoute>
          ),
        },
        {
          path: Links.Authorized.Repairs.route,
          element: (
            <ProtectedRoute allowedRoles={AllowedRoles.RepairsPage}>
              <Repairs />
            </ProtectedRoute>
          ),
        },
        {
          path: Links.Authorized.ServiceAndRepair.route,
          element: (
            <ProtectedRoute allowedRoles={AllowedRoles.ServiceAndRepairPage}>
              <ServiceAndRepair />
            </ProtectedRoute>
          ),
        },
        {
          path: Links.Authorized.TelehealthBilling.route,
          element: (
            <ProtectedRoute allowedRoles={AllowedRoles.TelehealthBillingPage}>
              <TelehealthBilling />
            </ProtectedRoute>
          ),
        },
        {
          path: Links.Authorized.PriceLists.route,
          element: (
            <ProtectedRoute allowedRoles={AllowedRoles.PriceListsPage}>
              <PriceLists />
            </ProtectedRoute>
          ),
        },
        {
          path: Links.Authorized.ServicePresentation.route,
          element: (
            <ProtectedRoute allowedRoles={AllowedRoles.ServicePresentationPage}>
              <ServicePresentation />
            </ProtectedRoute>
          ),
        },
        {
          path: Links.Authorized.TechUpdates.route,
          element: (
            <ProtectedRoute allowedRoles={AllowedRoles.TechUpdatesPage}>
              <TechUpdates />
            </ProtectedRoute>
          ),
        },
        {
          path: Links.Authorized.AbasDocuments.route,
          element: (
            <ProtectedRoute allowedRoles={AllowedRoles.AbasDocumentsPage}>
              <AbasDocuments />
            </ProtectedRoute>
          ),
        },
        {
          path: FeatureFlags.isMainboardReplacerEnabled()
            ? Links.Authorized.MainboardReplacerForm.route
            : undefined,
          element: (
            <ProtectedRoute
              allowedRoles={AllowedRoles.MainboardReplacerFormPage}
            >
              <MainboardReplacerForm />
            </ProtectedRoute>
          ),
        },
        {
          path: Links.Authorized.SNLots.route,
          element: (
            <ProtectedRoute allowedRoles={AllowedRoles.SNLotsPage}>
              <SNLots />
            </ProtectedRoute>
          ),
        },
        {
          path: Links.Authorized.WarrantyInfo.route,
          element: <WarrantyInfo />,
        },
        {
          path: FeatureFlags.isAdvancedSearchEnabled()
            ? Links.Authorized.AdvancedSearch.route
            : undefined,
          element: (
            <ProtectedRoute allowedRoles={AllowedRoles.AdvancedSearchPage}>
              <AdvancedSearch />
            </ProtectedRoute>
          ),
        },
        {
          path: Links.Authorized.CertificateInstruction.route,
          element: (
            <ProtectedRoute
              allowedRoles={AllowedRoles.CertificateInstructionPage}
            >
              <CertificateInstruction />
            </ProtectedRoute>
          ),
        },
        {
          path: Links.Authorized.AccountSettings.route,
          element: (
            <ProtectedRoute allowedRoles={AllowedRoles.AccountSettings}>
              <AccountSettings />
            </ProtectedRoute>
          ),
        },
        {
          path: Links.Unauthorized.Contacts.route,
          element: <ContactsPage />,
        },
        // { path: '', element: <ProductListPage /> },
      ],

      errorElement: (
        <QuerySuspenseErrorWrapper>
          <ReactRouterErrorBoundary />
        </QuerySuspenseErrorWrapper>
      ),
    },
    {
      path: Links.Unauthorized.ForgotPassword.route,
      element: <ForgotPasswordPage />,
    },
    {
      path: Links.Unauthorized.ResetPassword.route,
      element: <ResetPasswordPage />,
    },
  ]);
export const anonymousRoutes = () =>
  createBrowserRouter([
    {
      path: '*',
      element: UseCookieAuth ? <LoginPage /> : <ServerSideLoginPage />,
      ErrorBoundary: ReactRouterErrorBoundary,
    },
    {
      path: Links.Unauthorized.Contacts.route,
      element: <RootPage />,
      children: [
        {
          path: Links.Unauthorized.Contacts.route,
          element: <ContactsPage />,
        },
      ],
    },
    {
      path: Links.Unauthorized.ResetPassword.route,
      element: <ResetPasswordPage />,
    },
    {
      path: Links.Unauthorized.ForgotPassword.route,
      element: <ForgotPasswordPage />,
    },
  ]);
